import { useEffect, useState } from "react";
import api from "@/lib/api";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle,
} from "@/components/ui/dialog";
import { Envelope, Phone, Plus, PencilSimple, Trash, AddressBook, CaretDown } from "@phosphor-icons/react";
import { useConfirm } from "@/components/ConfirmDialog";

// FastAPI 422 returns `detail` as an array of {type, loc, msg, ...} objects.
// sonner toasts (and React in general) crash if you pass an object as a child,
// so normalise any axios error into a plain string before showing it.
const errorMessage = (err, fallback = "Something went wrong") => {
  const d = err?.response?.data?.detail;
  if (Array.isArray(d)) {
    return d.map((x) => x?.msg || (typeof x === "string" ? x : JSON.stringify(x))).join("; ");
  }
  if (typeof d === "string") return d;
  return err?.message || fallback;
};

/**
 * Per-firm contacts panel. Reusable across Firm 360° and Admin → Firm Edit dialog.
 *
 * Permissions: any signed-in user with access to the firm (backend enforces POC scoping
 * per call) can add / edit / delete contacts. Imported contacts get a small label.
 */
export default function ContactsPanel({ firmSubdomain, compact = false }) {
  const confirm = useConfirm();
  const [contacts, setContacts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showAdd, setShowAdd] = useState(false);
  const [editing, setEditing] = useState(null);
  const [expanded, setExpanded] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const r = await api.get(`/firms/${firmSubdomain}/contacts`);
      setContacts(r.data.contacts || []);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (firmSubdomain) load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [firmSubdomain]);

  const remove = async (c) => {
    if (!await confirm({
      title: `Delete contact "${c.name}"?`,
      confirmLabel: "Delete contact",
      destructive: true,
    })) return;
    try {
      await api.delete(`/firm-contacts/${c.contact_id}`);
      toast.success("Contact deleted");
      load();
    } catch (e) {
      toast.error(errorMessage(e, "Delete failed"));
    }
  };

  const padX = compact ? "px-4" : "px-5";
  const padY = compact ? "py-3" : "py-4";

  return (
    <div className="bg-white border border-gray-200 rounded-lg overflow-hidden" data-testid="contacts-panel">
      <button
        type="button"
        onClick={() => setExpanded((v) => !v)}
        className={`w-full ${padX} ${padY} border-b ${expanded ? "border-gray-100" : "border-transparent"} flex items-center justify-between hover:bg-gray-50/60 transition-colors text-left`}
        data-testid="contacts-toggle"
        aria-expanded={expanded}
      >
        <div className="flex items-center gap-2.5">
          <AddressBook size={compact ? 16 : 18} className="text-gray-700" />
          <h3 className={`font-display ${compact ? "text-sm" : "text-lg"} font-semibold text-gray-900`}>Contacts</h3>
          <span className="text-xs font-mono text-gray-400">· {contacts.length}</span>
        </div>
        <div className="flex items-center gap-2">
          {expanded && (
            <Button
              size="sm"
              variant="outline"
              onClick={(e) => { e.stopPropagation(); setEditing(null); setShowAdd(true); }}
              data-testid="contacts-add-btn"
            >
              <Plus size={14} className="mr-1.5" />
              Add contact
            </Button>
          )}
          <CaretDown
            size={14}
            className={`text-gray-400 transition-transform ${expanded ? "rotate-180" : ""}`}
          />
        </div>
      </button>

      {expanded && (loading ? (
        <div className={`${padX} py-8 text-center text-sm text-gray-400`}>Loading…</div>
      ) : contacts.length === 0 ? (
        <div className={`${padX} py-10 text-center text-sm text-gray-400`}>
          No contacts on file for this firm. Click "Add contact" to add one.
        </div>
      ) : (
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-gray-50 border-b border-gray-100">
              <tr className="text-left">
                <th className={`${padX} py-2.5 text-[11px] uppercase tracking-wider font-medium text-gray-500`}>Name</th>
                <th className="px-4 py-2.5 text-[11px] uppercase tracking-wider font-medium text-gray-500">Email</th>
                <th className="px-4 py-2.5 text-[11px] uppercase tracking-wider font-medium text-gray-500">Phone</th>
                <th className="px-4 py-2.5 w-20"></th>
              </tr>
            </thead>
            <tbody>
              {contacts.map((c) => (
                <tr
                  key={c.contact_id}
                  className="border-b border-gray-100 last:border-b-0 hover:bg-gray-50/60"
                  data-testid={`contact-row-${c.contact_id}`}
                >
                  <td className={`${padX} py-3`}>
                    <div className="font-medium text-gray-900">{c.name || "—"}</div>
                    {c.source === "imported" && (
                      <div className="text-[9px] uppercase tracking-wider text-gray-400 mt-0.5">Imported</div>
                    )}
                  </td>
                  <td className="px-4 py-3">
                    {c.email ? (
                      <a
                        href={`mailto:${c.email}`}
                        className="inline-flex items-center gap-1.5 text-blue-700 hover:text-blue-900 hover:underline text-xs font-mono"
                      >
                        <Envelope size={13} />
                        {c.email}
                      </a>
                    ) : (
                      <span className="text-xs text-gray-300">—</span>
                    )}
                  </td>
                  <td className="px-4 py-3">
                    {c.phone ? (
                      <a
                        href={`tel:${c.phone.replace(/\s+/g, "")}`}
                        className="inline-flex items-center gap-1.5 text-emerald-700 hover:text-emerald-900 hover:underline text-xs font-mono"
                      >
                        <Phone size={13} weight="fill" />
                        {c.phone}
                      </a>
                    ) : (
                      <span className="text-xs text-gray-300">—</span>
                    )}
                  </td>
                  <td className="px-4 py-3 text-right whitespace-nowrap">
                    <Button
                      size="sm"
                      variant="ghost"
                      onClick={() => { setEditing(c); setShowAdd(true); }}
                      className="h-7 px-2"
                      data-testid={`contact-edit-${c.contact_id}`}
                    >
                      <PencilSimple size={13} />
                    </Button>
                    <Button
                      size="sm"
                      variant="ghost"
                      onClick={() => remove(c)}
                      className="h-7 px-2 text-red-600 hover:bg-red-50"
                      data-testid={`contact-delete-${c.contact_id}`}
                    >
                      <Trash size={13} />
                    </Button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      ))}

      <ContactDialog
        open={showAdd}
        onClose={() => setShowAdd(false)}
        onSaved={() => { setShowAdd(false); load(); }}
        firmSubdomain={firmSubdomain}
        contact={editing}
      />
    </div>
  );
}

function ContactDialog({ open, onClose, onSaved, firmSubdomain, contact }) {
  const isEdit = !!contact;
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [phone, setPhone] = useState("");
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    if (open) {
      setName(contact?.name || "");
      setEmail(contact?.email || "");
      setPhone(contact?.phone || "");
    }
  }, [open, contact]);

  const valid = name.trim() && (email.trim() || phone.trim());

  const save = async () => {
    if (!valid) return;
    setSubmitting(true);
    try {
      const payload = {
        name: name.trim(),
        email: email.trim() || null,
        phone: phone.trim() || null,
      };
      if (isEdit) {
        await api.patch(`/firm-contacts/${contact.contact_id}`, payload);
        toast.success("Contact updated");
      } else {
        await api.post(`/firms/${firmSubdomain}/contacts`, payload);
        toast.success("Contact added");
      }
      onSaved();
    } catch (e) {
      toast.error(errorMessage(e, "Save failed"));
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="max-w-md" data-testid="contact-dialog">
        <DialogHeader>
          <DialogTitle className="font-display flex items-center gap-2">
            <AddressBook size={18} className="text-gray-700" />
            {isEdit ? "Edit contact" : "Add contact"}
          </DialogTitle>
        </DialogHeader>
        <div className="space-y-3 mt-2">
          <div>
            <label className="text-[11px] uppercase tracking-wider text-gray-500 font-medium">Name</label>
            <Input value={name} onChange={(e) => setName(e.target.value)} placeholder="Full name" data-testid="contact-name" className="mt-1" />
          </div>
          <div>
            <label className="text-[11px] uppercase tracking-wider text-gray-500 font-medium">Email</label>
            <Input value={email} onChange={(e) => setEmail(e.target.value)} placeholder="name@example.com" data-testid="contact-email" className="mt-1" />
          </div>
          <div>
            <label className="text-[11px] uppercase tracking-wider text-gray-500 font-medium">Phone</label>
            <Input value={phone} onChange={(e) => setPhone(e.target.value)} placeholder="+91 9XXXXXXXXX" data-testid="contact-phone" className="mt-1" />
          </div>
          <div className="text-[10px] text-gray-500">
            At least one of email or phone is required.
          </div>
        </div>
        <div className="flex items-center justify-end gap-2 mt-4">
          <Button variant="ghost" onClick={onClose}>Cancel</Button>
          <Button
            onClick={save}
            disabled={!valid || submitting}
            className="bg-gray-900 text-white hover:bg-gray-800"
            data-testid="contact-save"
          >
            {submitting ? "Saving…" : isEdit ? "Save changes" : "Add contact"}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
